import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ImagemModal({ show, imagem, onClose, onSave }) {
  const [formData, setFormData] = useState({
    idEntidade: '',
    orientacao: 'R',
    img: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  
  // Memorizar vinculo para evitar loop infinito
  const vinculo = useMemo(() => {
    const stored = localStorage.getItem('vinculo');
    return stored ? JSON.parse(stored) : null;
  }, []);
  
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  // Separar o efeito de inicialização para evitar loops
  useEffect(() => {
    if (!show) return;
    
    console.log('Modal aberto, inicializando formData');
    
    if (imagem) {
      console.log('Editando imagem existente');
      setFormData({
        idEntidade: imagem.idEntidade || '',
        orientacao: imagem.orientacao || 'R',
        img: imagem.img || null,
      });
      if (imagem.img) {
        setImagePreview(`data:image/png;base64,${imagem.img}`);
      }
    } else {
      console.log('Nova imagem, idEntidade:', vinculo?.idEntidade);
      setFormData({
        idEntidade: vinculo?.idEntidade || '',
        orientacao: 'R',
        img: null,
      });
      setImagePreview(null);
    }
    setValidated(false);
    
    // Reset do input file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [show]); // Apenas show na dependência para evitar loops

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const processImage = (file) => {
    console.log('Processando arquivo:', file?.name, 'Tipo:', file?.type, 'Tamanho:', file?.size);
    
    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      showDefaultModal('error', 'Por favor, selecione um arquivo de imagem válido.');
      return;
    }

    // Validar tamanho do arquivo (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showDefaultModal('error', 'A imagem deve ter no máximo 5MB.');
      return;
    }

    // Converter para Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const dataUrl = reader.result;
        console.log('DataURL recebido. Tamanho:', dataUrl?.length);
        
        // Extrair Base64: dividir por vírgula e pegar a segunda parte
        const parts = dataUrl.split(',');
        const base64String = parts.length > 1 ? parts[1] : dataUrl;
        
        console.log('Base64 extraído. Tamanho:', base64String?.length);
        
        // Atualizar formData
        setFormData(prev => {
          const updated = {
            ...prev,
            img: base64String,
          };
          console.log('FormData atualizado. Img presente:', !!updated.img, 'Tamanho:', updated.img?.length);
          return updated;
        });
        setImagePreview(dataUrl);
      } catch (error) {
        console.error('Erro ao processar Base64:', error);
        showDefaultModal('error', 'Erro ao processar a imagem.');
      }
    };
    reader.onerror = () => {
      console.error('Erro ao ler o arquivo');
      showDefaultModal('error', 'Erro ao ler o arquivo de imagem.');
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    console.log('handleFileChange disparado');
    const file = e.target.files?.[0];
    console.log('Arquivo capturado:', file?.name);
    if (file) {
      processImage(file);
    }
    // Sempre resetar o input para permitir selecionar o mesmo arquivo novamente
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processImage(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = () => {
    console.log('Clique disparado, abrindo seletor de arquivo');
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({
      ...prev,
      img: null,
    }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const form = e.currentTarget;

    console.log('Form submit. FormData:', {
        idEntidade: formData.idEntidade,
      orientacao: formData.orientacao,
      imgPresente: !!formData.img,
      imgTamanho: formData.img?.length
    });

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!formData.img) {
      console.error('Erro ao salvar: formData.img está null ou undefined');
      showDefaultModal('error', 'Por favor, selecione uma imagem.');
      return;
    }

    setLoading(true);

    const url = imagem
      ? `${API_BASE_URL.IMAGEM}/update`
      : `${API_BASE_URL.IMAGEM}/save`;

    const method = imagem ? 'put' : 'post';

    console.log('Enviando para API:', {
      url,
      method,
      formData: {
      idEntidade: formData.idEntidade,
        orientacao: formData.orientacao,
        imgTamanho: formData.img?.length
      }
    });

    axios({
      method: method,
      url: url,
      data: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(response => {
        showDefaultModal('success', imagem ? 'Imagem atualizada com sucesso!' : 'Imagem cadastrada com sucesso!');
        onSave();
        onClose();
      })
      .catch(error => {
        console.error('Erro ao salvar imagem:', error);
        showDefaultModal('error', error.response?.data?.message || 'Erro ao salvar imagem!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div
              className="modal-header text-white"
              style={{
                background:
                  'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
                padding: '0.5rem 1rem',
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                border: 'none',
              }}
            >
              <h6 className="modal-title mb-0">
                <i className="bi bi-image me-2"></i>
                {imagem ? 'Editar Imagem' : 'Nova Imagem'}
              </h6>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            <div className="modal-body">
              <form
                noValidate
                validated={validated.toString()}
                onSubmit={handleSubmit}
                id="imagemForm"
              >
                <div className="row g-3">
                  {/* CNPJ Entidade - Campo oculto */}
                  <input
                    type="hidden"
                    name="idEntidade"
                    value={formData.idEntidade}
                  />

                  {/* Orientação */}
                  <div className="col-md-12">
                    <label htmlFor="orientacao" className="form-label">
                      Orientação <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="orientacao"
                      name="orientacao"
                      value={formData.orientacao}
                      onChange={handleChange}
                      required
                      disabled={!!imagem}
                    >
                      <option value="R">Retrato (Vertical)</option>
                      <option value="P">Paisagem (Horizontal)</option>
                    </select>
                    <div className="invalid-feedback">Campo obrigatório.</div>
                  </div>

                  {/* Upload de Imagem */}
                  <div className="col-12">
                    <label className="form-label">
                      Imagem <span className="text-danger">*</span>
                      {formData.img && (
                        <span className="badge bg-success ms-2">
                          <i className="bi bi-check-circle me-1"></i>
                          Carregada ({Math.round(formData.img.length / 1024)}KB)
                        </span>
                      )}
                    </label>
                    
                    {/* Input file invisível */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />

                    {/* Área de upload */}
                    <div
                      className="border rounded p-4 text-center"
                      style={{
                        borderStyle: 'dashed',
                        borderColor: '#dee2e6',
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={handleClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleClick();
                        }
                      }}
                    >
                      {imagePreview ? (
                        <div className="position-relative d-inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview da imagem"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                            onClick={handleRemoveImage}
                            title="Remover imagem"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                          <div className="mt-3">
                            <small className="text-muted d-block">
                              Clique para selecionar outra imagem
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: '20px' }}>
                          <i
                            className="bi bi-cloud-upload"
                            style={{ fontSize: '3rem', color: '#6c757d', display: 'block', marginBottom: '10px' }}
                          ></i>
                          <p className="mb-2" style={{ fontSize: '1rem', fontWeight: '500' }}>
                            Selecione uma imagem
                          </p>
                          <p className="mb-3" style={{ color: '#6c757d' }}>
                            Arraste e solte um arquivo aqui ou clique para procurar
                          </p>
                          <small className="text-muted d-block">
                            Formatos aceitos: PNG, JPG, JPEG (máx. 5MB)
                          </small>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm mt-3"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick();
                            }}
                          >
                            <i className="bi bi-folder2-open me-1"></i>
                            Procurar Arquivo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="imagemForm"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default ImagemModal;
